import { useCallback, useEffect, useState } from "react";
import { ManualFlightsHeader } from "../components/ManualFlightsHeader/ManualFlightsHeader";
import { FileUploadSection } from "../components/FileUploadSection/FileUploadSection";
import { FlightsPreviewTable } from "../components/FlightsPreviewTable/FlightsPreviewTable";
import {
  cancelManualFlight,
  deleteManualFlight,
  getManualFlights,
  publishManualFlight,
} from "../api/ManualFlightsApi";
import {
  getAirports,
  getFilters,
  type Airport,
  type SystemDictionary,
} from "@/shared/api/filters";
import type {
  ManualFlight,
  ManualFlightsResponse,
} from "../types/manual-flight.types";
import { OrdersHeader } from "@/features/orders/components/OrdersHeader/OrdersHeader";
import { ManualFlightsTable } from "../components/manual-flights-table/ManualFlightsTable";
import { ManualFlightsFilters } from "../components/manual-flights-filters/ManualFlightsFilters";
import { ManualFlightsModal } from "../components/ManualFlightsModal/ManualFlightsModal";
import { toast } from "sonner";

export interface ManualFlightdFiltersState {
  airline: string;
  status: string;
  page: number;
  paymentStatus: string;
  pageSize: number;
  dateFrom: string;
  dateTo: string;
  fromIata: string;
  toIata: string;
}

export function ManualFlightsPage() {
  const [previewFlights, setPreviewFlights] = useState<ManualFlight[]>([]);
  const [paginatedResponse, setPaginatedResponse] =
    useState<ManualFlightsResponse>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dictionary, setDictionary] = useState<SystemDictionary | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [airports, setAirports] = useState<Airport[]>([]);

  const [filters, setFilters] = useState<ManualFlightdFiltersState>({
    airline: "",
    status: "",
    page: 1,
    paymentStatus: "",
    pageSize: 20,
    dateFrom: "",
    dateTo: "",
    toIata: "",
    fromIata: "",
  });

  const handleDelete = async (ids: string[]) => {
    try {
      await Promise.all(ids.map((id) => deleteManualFlight(Number(id))));
      toast.success("Рейс удален");
      fetchManualFlights();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Не удалось добавить пользователя";
      toast.error(message);
      console.error(error);
    }
  };
  const handleCancel = async (ids: string[]) => {
    try {
      await Promise.all(ids.map((id) => cancelManualFlight(Number(id))));
      toast.success("Рейс скрыт");
      fetchManualFlights();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Не удалось добавить пользователя";
      toast.error(message);
      console.error(error);
    }
  };
  const handlePublish = async (ids: string[]) => {
    try {
      await Promise.all(ids.map((id) => publishManualFlight(Number(id))));
      toast.success("Рейс опубликован");
      fetchManualFlights();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Не удалось добавить пользователя";
      toast.error(message);
      console.error(error);
    }
  };

  const fetchManualFlights = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const res = await getManualFlights(filters);
      const airports = await getAirports();
      setAirports(airports);
      setPaginatedResponse(res);
      setPreviewFlights(res.data);

      const dict = await getFilters();
      setDictionary(dict);
    } catch (e) {
      console.error(e);
      setError("Не удалось загрузить рейсы");
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchManualFlights();
  }, [fetchManualFlights]);

  return (
    <div className="max-w-[1400px] mx-auto">
      <ManualFlightsHeader onOpenChange={setIsModalOpen} />

      <ManualFlightsFilters
        totalCount={paginatedResponse?.total ?? 0}
        filteredCount={previewFlights.length}
        dictionary={dictionary}
        setFilters={setFilters}
        filters={filters}
        airports={airports}
      />

      <ManualFlightsTable
        previewFlights={previewFlights}
        totalPages={paginatedResponse?.totalPages ?? 1}
        currentPage={paginatedResponse?.page ?? 1}
        onDelete={(ids) => handleDelete(ids)}
        onHide={(ids) => handleCancel(ids)}
        onPublish={(ids) => handlePublish(ids)}
        page={filters.page}
        pageSize={filters.pageSize}
        onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
      />

      <ManualFlightsModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onImported={fetchManualFlights}
      />
    </div>
  );
}
